import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { askAiTokens as t } from "./tokens";
import { useIsMobile } from "./useMediaQuery";

interface Props {
  open: boolean;
  onClose?: () => void;
  children: React.ReactNode;
}

const SidePanel: React.FC<Props> = ({ open, onClose, children }) => {
  const isMobile = useIsMobile();

  // ─── Mobile: slide-over drawer + tap-to-close backdrop ───
  if (isMobile) {
    return (
      <AnimatePresence initial={false}>
        {open && (
          <>
            <motion.div
              key="sp-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={onClose}
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(15, 23, 42, 0.42)",
                zIndex: 50,
              }}
            />
            <motion.aside
              key="sp-panel"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                width: "min(360px, 88vw)",
                background: t.color.surface,
                borderLeft: `1px solid ${t.color.border}`,
                boxShadow: "-12px 0 32px -12px rgba(15, 23, 42, 0.2)",
                display: "flex",
                flexDirection: "column",
                zIndex: 51,
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {children}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    );
  }

  // ─── Desktop: existing inline flex column with width animation ───
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.aside
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: t.layout.sidePanelWidth, opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          style={{
            background: t.color.surface,
            borderLeft: `1px solid ${t.color.border}`,
            overflow: "hidden",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <div
            style={{
              width: t.layout.sidePanelWidth,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {children}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
};

export default SidePanel;
